import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SDLC_STAGES } from '../stages';
import { getProject, setStageApproval, setStageComment, subscribe } from '../../../services/sdlcProject.service';
import './StageShell.css';

/**
 * Generic two-pane layout used by every SDLC stage.
 *
 * Props:
 *  - stageId       : id of the current stage (must match an entry in SDLC_STAGES)
 *  - title         : main heading (defaults to stage label)
 *  - description   : sub-heading
 *  - inputs        : array of { label, value } shown in the left "from previous stages" panel
 *  - actions       : node rendered in the header right (e.g. Generate button)
 *  - children      : main pane content
 *  - canContinue   : whether the "Continue to next stage" button is enabled
 *  - continueLabel : optional override for the next-stage button label
 */
function StageShell({
  stageId,
  title,
  description,
  inputs = [],
  actions,
  children,
  canContinue = true,
  continueLabel,
}) {
  const navigate = useNavigate();
  const [project, setProject] = useState(getProject());
  const idx = SDLC_STAGES.findIndex((s) => s.id === stageId);
  const stage = idx >= 0 ? SDLC_STAGES[idx] : null;
  const prev = idx > 0 ? SDLC_STAGES[idx - 1] : null;
  const next = idx >= 0 && idx < SDLC_STAGES.length - 1 ? SDLC_STAGES[idx + 1] : null;
  const approval = project?.[stageId]?.approval || {};
  const approved = Boolean(approval.approved);
  const comment = approval.comment || '';

  useEffect(() => subscribe(setProject), []);

  const handleCommentChange = (event) => {
    setProject(setStageComment(stageId, event.target.value));
  };

  const handleApprovalToggle = () => {
    setProject(setStageApproval(stageId, !approved, comment));
  };

  return (
    <div className="stage-shell">
      <aside className="stage-shell-side">
        <h3>From previous stages</h3>
        {inputs.length === 0 && (
          <div className="empty">No upstream inputs yet — this is the first stage.</div>
        )}
        {inputs.map((inp, i) => (
          <div className="input-block" key={i}>
            <div className="label">{inp.label}</div>
            <div className="value">{inp.value || <span className="empty">— not provided —</span>}</div>
          </div>
        ))}
      </aside>

      <section className="stage-shell-main">
        <header className="stage-shell-header">
          <div>
            <h2>
              {stage?.icon} {title || stage?.label}
            </h2>
            {description && <p>{description}</p>}
            {stage?.agent && (
              <div className="stage-agent-meta">
                <span>{stage.agent}</span>
                <span>{stage.artifact}</span>
              </div>
            )}
          </div>
          <div>{actions}</div>
        </header>

        <div>{children}</div>

        <section className="stage-approval-panel" aria-label="Human approval gate">
          <div>
            <h3>Approval Gate</h3>
            <p>Approve this stage to allow the next agent to continue, or leave a comment before regenerating.</p>
          </div>
          <textarea
            value={comment}
            onChange={handleCommentChange}
            placeholder="Optional comment for the next agent or for rework..."
            rows={3}
          />
          <button
            type="button"
            className={`stage-btn ${approved ? 'approved' : 'primary'}`}
            onClick={handleApprovalToggle}
            disabled={!canContinue}
          >
            {approved ? 'Approved' : 'Approve Stage'}
          </button>
        </section>

        <footer className="stage-shell-footer">
          <button
            type="button"
            className="stage-btn"
            disabled={!prev}
            onClick={() => prev && navigate(`/tools/sdlc/${prev.id}`)}
          >
            ← {prev ? prev.label : 'Previous'}
          </button>
          <button
            type="button"
            className="stage-btn primary"
            disabled={!next || !canContinue || !approved}
            onClick={() => next && navigate(`/tools/sdlc/${next.id}`)}
            title={!canContinue ? 'Complete this stage before continuing' : !approved ? 'Approve this stage before continuing' : undefined}
          >
            {continueLabel || (next ? `Continue to ${next.label}` : 'Done')} →
          </button>
        </footer>
      </section>
    </div>
  );
}

export default StageShell;
